import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Caution, Dialog, Field, SECONDARY_BUTTON } from '@/features/inventory/registerUi';
import { classifyClearError, useClearDevice } from './useClearance';

/**
 * SLICE 14c — CLEAR A TRACKER FOR COMMANDING.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * 🔴 THIS RECORDS AN ATTESTATION. THE DIALOG SAYS SO, IN WORDS, BEFORE ANY PRESS.
 * ─────────────────────────────────────────────────────────────────────────────
 * A super-admin says "I accept the risk of commanding this tracker before the
 * machine proof exists." It hardens nothing and proves nothing. The intro says
 * exactly that so the person clearing cannot mistake it for hardening — the same
 * confusion the whole feature exists to prevent, aimed here at the person MAKING
 * the record rather than the one reading it later.
 *
 * 🔴 THE NOTE FIELD EXPLAINS WHY IT MATTERS, NOT JUST THAT IT IS REQUIRED. The
 * server refuses a note under 10 characters (422 `note_required`), but a field
 * that only says "required" invites "ok". The hint says the note is the whole
 * worth of the record a month from now, and gives the shape of a real one. The
 * 422 is surfaced as its own sentence, not a generic error.
 *
 * 🔴 THE SMS CAVEAT IS ON THE FORM, because a reader might over-conclude. The
 * attestation being recorded today is that the tracker's authorised-number list
 * was set — an SMS/call restriction the device does NOT apply to commands
 * arriving over the mobile network from the server. It closes one door and leaves
 * the other where it was, and the operator should know that as they sign.
 */
const NOTE =
  'w-full min-h-[5rem] rounded-lg border border-raad-border bg-raad-input px-3 py-2 text-[13.5px] text-raad-text outline-none focus:border-raad-primary';

export function ClearForCommandingDialog({
  device,
  onClose,
}: {
  device: { deviceId: string; imei: string };
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const clear = useClearDevice();

  // Mirrors the server floor (min 10 after trim). The button stays disabled
  // below it; the 422 branch is the belt to this suspenders, for a note that is
  // 10 spaces or is rejected for another reason later.
  const ready = note.trim().length >= 10;

  async function submit() {
    if (!ready) return;
    setError(null);
    try {
      await clear.mutateAsync({ deviceId: device.deviceId, note: note.trim() });
      setDone(true);
    } catch (e) {
      setError(t(`clearance.clear.error.${classifyClearError(e)}`));
    }
  }

  return (
    <Dialog title={t('clearance.clear.title', { imei: device.imei })} onClose={onClose} wide>
      {done ? (
        <div className="grid gap-3">
          <p className="rounded-lg border border-raad-warn/40 bg-raad-warn/10 p-3 text-[12.5px] text-raad-text">
            {t('clearance.clear.done', { imei: device.imei })}
          </p>
          <div className="flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className="min-h-10 rounded-lg bg-raad-primary px-4 text-[13px] font-semibold text-raad-onprimary"
            >
              {t('clearance.clear.doneButton')}
            </button>
          </div>
        </div>
      ) : (
        <div className="grid gap-3">
          <p className="text-[13px]">{t('clearance.clear.intro')}</p>
          <Caution>{t('clearance.clear.smsCaveat')}</Caution>

          <Field label={t('clearance.clear.noteLabel')} hint={t('clearance.clear.noteHint')}>
            <textarea
              className={NOTE}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              autoFocus
              rows={3}
            />
          </Field>

          {error ? (
            <p
              role="alert"
              className="rounded-lg border border-raad-error/40 bg-raad-error/10 p-3 text-[12.5px] text-raad-text"
            >
              {error}
            </p>
          ) : null}

          <div className="mt-2 flex items-center justify-end gap-3">
            <button type="button" className={SECONDARY_BUTTON} onClick={onClose}>
              {t('inventory.close')}
            </button>
            <button
              type="button"
              onClick={() => void submit()}
              disabled={!ready || clear.isPending}
              className="min-h-10 rounded-lg bg-raad-primary px-4 text-[13px] font-semibold text-raad-onprimary disabled:opacity-40"
            >
              {clear.isPending ? t('clearance.clear.working') : t('clearance.clear.submit')}
            </button>
          </div>
        </div>
      )}
    </Dialog>
  );
}
